import { useEffect, useRef } from 'react';

/**
 * Custom hook for managing the modal category behavior.
 */

interface TouchData {
  startY: number;
  lastY: number;
  lastTime: number;
  swipeSpeed: number;
  elementHeight: number;
  top: number;
}

enum ModalActionsEnum {
  OPEN = 'OPEN',
  CLOSE = 'CLOSE',
  INITIAL_SIZE = 'INITIAL_SIZE',
  NONE = 'NONE'
}

type Props = {
  onClose: () => void;
  isOpen: boolean;
};

const SLOW_SWIPE_THRESHOLD = 14; // Threshold for slow swipe
const FAST_SWIPE_THRESHOLD = 7; // Threshold for fast swipe
const FAST_SWIPE_VELOCITY = 0.8; // Threshold velocity for a fast swipe (px/ms)
const TOP_OFFSET = 50;

export const useDrawerPosition = ({ onClose, isOpen }: Props) => {
  // Refs for DOM elements
  const modalRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  // Refs for tracking state
  const initialHeaderRect = useRef<DOMRect | null>(null);
  const initialRect = useRef<{
    headerRect: DOMRect;
    contentRect: DOMRect;
    footerRect?: DOMRect;
  } | null>(null);

  const touchData = useRef<TouchData>({
    startY: 0,
    lastY: 0,
    lastTime: 0,
    swipeSpeed: 0,
    elementHeight: 0,
    top: 0
  });

  const getScreenHeight = () =>
    document.documentElement.clientHeight ?? window.innerHeight;

  const calculatePositionPercent = (startY: number, lastY: number) => {
    const clientHeight = getScreenHeight();
    return {
      startY: (1 - startY / clientHeight) * 100,
      lastY: (1 - lastY / clientHeight) * 100
    };
  };

  // Evaluates the swipe direction and whether it meets the necessary criteria to open or close the modal.
  const getSwipeModalAction = (
    startY: number,
    lastY: number,
    dispersion: number
  ): ModalActionsEnum => {
    const swipeDistance = startY - lastY;
    const absoluteSwipeDistance = Math.abs(swipeDistance);
    const isEnoughSwipe = absoluteSwipeDistance > dispersion;

    // Check if swipe down with enough distance to close
    const shouldClose = swipeDistance > 0 && isEnoughSwipe;

    return shouldClose ? ModalActionsEnum.CLOSE : ModalActionsEnum.INITIAL_SIZE;
  };

  const handleClose = () => {
    if (!modalRef.current) return;
    modalRef.current.style.transform = 'translateY(100%)';
    modalRef.current.addEventListener('transitionend', onClose, {
      once: true
    });
  };

  const getInitialHeaderRect = () => {
    if (headerRef.current)
      initialHeaderRect.current = headerRef.current.getBoundingClientRect();
  };

  // Handles the start of a touch event, initializing touch data.
  const handleTouchStart = (e: TouchEvent) => {
    if (!modalRef.current) return;
    const rect = modalRef.current.getBoundingClientRect();
    const y = e.touches[0].clientY;

    touchData.current = {
      startY: y,
      lastY: y,
      lastTime: Date.now(),
      swipeSpeed: 0,
      elementHeight: rect.height,
      top: rect.top
    };
  };

  // Handles the touch move event, calculating swipe speed and updating the modal position.
  const handleTouchMove = (e: TouchEvent) => {
    if (!modalRef.current) return;
    const { lastY, lastTime, elementHeight } = touchData.current;
    const currentTime = Date.now();
    const y = e.touches[0].clientY;

    // Prevent scrolling above header position
    if (initialHeaderRect.current && y < initialHeaderRect.current.top) return;

    // Calculate swipe speed
    const swipeDistance = y - lastY;
    const deltaTime = currentTime - lastTime;

    if (deltaTime > 0) {
      touchData.current.swipeSpeed = Math.abs(swipeDistance / deltaTime);
    }

    // Update touch data
    touchData.current.lastY = y;
    touchData.current.lastTime = currentTime;

    // Update modal position
    const clientHeight = getScreenHeight();
    const translate = y > 0 ? clientHeight - y - elementHeight : 0;
    modalRef.current.style.transform = `translateY(${translate * -1}px)`;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    requestAnimationFrame(() => {
      if (!modalRef.current || !touchData.current || !contentRef.current)
        return;

      // Get references to the modal and content styles.
      const { style: modalStyle } = modalRef.current;
      // const { style: contentStyle } = contentRef.current;

      // Extract swipe speed and starting Y position from touch data.
      const { swipeSpeed, startY } = touchData.current;

      // Get the last Y position of the touch event.
      const lastY = e.changedTouches[0].clientY;

      // Determine threshold based on swipe speed
      const conditionThreshold =
        swipeSpeed > FAST_SWIPE_VELOCITY
          ? FAST_SWIPE_THRESHOLD
          : SLOW_SWIPE_THRESHOLD;

      const positionPercent = calculatePositionPercent(startY, lastY);

      const actions = getSwipeModalAction(
        positionPercent.startY,
        positionPercent.lastY,
        conditionThreshold
      );

      // Function to perform modal actions based on the determined action.
      const executeModalAction = (action: ModalActionsEnum) => {
        if (action === ModalActionsEnum.CLOSE && modalRef.current) {
          handleClose();
          return;
        }

        if (action === ModalActionsEnum.INITIAL_SIZE) {
          const initialSize = initialRect.current;
          const initialFooterHeight = initialSize?.footerRect?.height ?? 0;
          const initialContentHeight = initialSize?.contentRect.height ?? 0;
          const initialHeaderHeight = initialSize?.headerRect.height ?? 0;

          const summaryHeight =
            initialHeaderHeight + initialContentHeight + initialFooterHeight;

          const clientHeight = getScreenHeight();

          const translateCompute =
            summaryHeight >= clientHeight
              ? TOP_OFFSET
              : clientHeight - summaryHeight;

          modalStyle.transform = `translateY(${translateCompute}px)`;
        }
      };

      executeModalAction(actions);
    });
  };

  useEffect(() => {
    if (!headerRef.current || !modalRef.current || !contentRef.current) return;
    if (!isOpen) return;

    let headerHeight: number = initialRect?.current?.headerRect?.height ?? 0;
    let contentHeight: number = initialRect?.current?.contentRect?.height ?? 0;

    modalRef.current.addEventListener('transitionend', getInitialHeaderRect, {
      once: true
    });

    if (!initialRect.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      const contentRect = contentRef.current.getBoundingClientRect();
      const footerRect = footerRef.current?.getBoundingClientRect();

      initialRect.current = { ...{ headerRect, contentRect, footerRect } };

      headerHeight = headerRect.height;
      contentHeight = contentRect.height;
    }

    const computeInitialHeight = () => {
      if (!contentRef.current || !modalRef.current || !headerRef.current)
        return;

      let footerHeight = 0;
      if (footerRef.current) {
        footerHeight = footerRef.current.getBoundingClientRect().height;
      }

      const computedHeight =
        headerHeight + contentHeight + footerHeight - TOP_OFFSET;

      const clientHeight = getScreenHeight();
      const isFullWidth = computedHeight >= clientHeight;

      const computedContentHeight =
        modalRef.current.getBoundingClientRect().height -
        headerHeight -
        footerHeight -
        TOP_OFFSET;
      contentRef.current.style.height = `${computedContentHeight}px`;

      const heightClient = getScreenHeight();
      modalRef.current.style.transform = isFullWidth
        ? `translateY(${TOP_OFFSET}px)`
        : `translateY(${heightClient - computedHeight - TOP_OFFSET}px)`;
    };

    computeInitialHeight();

    const events = {
      touchstart: handleTouchStart,
      touchmove: handleTouchMove,
      touchend: handleTouchEnd
    };
    type EventTypes = keyof typeof events;
    Object.entries(events).forEach(([key, value]) => {
      headerRef.current?.addEventListener(key as EventTypes, value);
    });

    if (window.visualViewport)
      window.visualViewport.addEventListener('resize', computeInitialHeight);

    return () => {
      if (window.visualViewport)
        return window.visualViewport.removeEventListener(
          'resize',
          computeInitialHeight
        );

      if (!headerRef.current) return;
      Object.entries(events).forEach(([key, value]) => {
        headerRef.current?.removeEventListener(key as EventTypes, value);
      });
    };
  }, [headerRef.current, modalRef.current, isOpen]);

  return { modalRef, headerRef, footerRef, contentRef, handleClose };
};
